import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { FileUploadZone } from '../pilgrimGuide/upload/FileUploadZone';
import { ValidationErrors } from '../pilgrimGuide/upload/ValidationErrors';
import { WarningMessage } from '../pilgrimGuide/upload/WarningMessage';
import { Download, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUpgradeStore } from '../../stores/upgradeStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const BulkAnswerModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { requests } = useUpgradeStore();

  // Function to create template Excel file based on current data
  const handleDownloadTemplate = () => {
    // Get all pending requests (by default, filter for admin)
    const pendingRequests = Object.values(requests).filter(req => req.status === 'pending');
    const approvedRequests = Object.values(requests).filter(req => req.status === 'approved' || req.status === 'payment_pending');

    // Combine both pending and approved requests
    const allRelevantRequests = [...pendingRequests, ...approvedRequests];

    // Map requests to template format
    const template = allRelevantRequests.map(request => ({
      // Key matching fields for identification
      passport_number: request.passportNumber,
      hotel_name: request.fromHotel,
      
      // Information fields
      pilgrim_name: request.pilgrimName,
      from_room_type: request.fromRoomType,
      to_room_type: request.toRoomType || 'double',
      
      // Decision fields
      status: request.status === 'approved' || request.status === 'payment_pending' ? 'change' : '', // 'approve', 'reject', or 'change' (for already approved)
      price: request.status === 'approved' || request.status === 'payment_pending' ? request.price : '', // Suggested price (SAR)
      reason: '', // Only needed for rejection
      
      // If already approved, include the previous price
      previous_price: request.status === 'approved' || request.status === 'payment_pending' ? request.price : ''
    }));

    const ws = XLSX.utils.json_to_sheet(template);

    // Add descriptive headers
    XLSX.utils.sheet_add_aoa(ws, [[
      'Passport Number',
      'Hotel Name',
      'Pilgrim Name',
      'From Room Type',
      'To Room Type',
      'Status (approve, reject, or change)',
      'Price (SAR)',
      'Rejection Reason',
      'Previous Price'
    ]], { origin: 'A1' });

    // Add notes in rows below the data
    const lastRowIndex = template.length + 2; // +2 for header and 0-indexing
    XLSX.utils.sheet_add_aoa(ws, [[
      'INSTRUCTIONS:',
      '',
      'For NEW requests: Enter "approve" or "reject" in the Status column. If approving, enter the price in SAR.'
    ]], { origin: `A${lastRowIndex}` });

    XLSX.utils.sheet_add_aoa(ws, [[
      'For ALREADY APPROVED requests: Enter "change" in the Status column and specify the new price. The previous price is shown for reference.'
    ]], { origin: `A${lastRowIndex + 1}` });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // Passport Number
      { wch: 25 }, // Hotel Name
      { wch: 30 }, // Pilgrim Name
      { wch: 15 }, // From Room Type
      { wch: 15 }, // To Room Type
      { wch: 25 }, // Status
      { wch: 15 }, // Price
      { wch: 30 }, // Rejection Reason
      { wch: 15 }, // Previous Price
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-answer-template.xlsx');
  };

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (data) {
        // Close modal first
        onClose();
        
        // Navigate to status page with file data
        navigate('/bulk-answer-status', {
          state: { fileData: data }
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.roomUpgrade?.bulkAnswer?.title || 'Bulk Answer Upgrade Requests'}
    >
      <div className="space-y-6">
        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.roomUpgrade?.bulkAnswer?.downloadTemplate || 'Download Excel Template'}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />
        
        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg text-blue-700">
          <h4 className="font-medium mb-2">
            {translations?.roomUpgrade?.bulkAnswer?.instructions?.title || 'Instructions'}
          </h4>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>
              {translations?.roomUpgrade?.bulkAnswer?.instructions?.format || 
                'Use "approve", "reject", or "change" in the status column'}
            </li>
            <li>
              {translations?.roomUpgrade?.bulkAnswer?.instructions?.hotelName ||
                'The Hotel Name and Passport Number are used to match requests'}
            </li>
            <li>
              {translations?.roomUpgrade?.bulkAnswer?.instructions?.price || 
                'For approvals and changes, specify the exact price in SAR'}
            </li>
            <li>
              {translations?.roomUpgrade?.bulkAnswer?.instructions?.reason || 
                'For rejections, provide a reason'}
            </li>
            <li>
              {translations?.roomUpgrade?.bulkAnswer?.instructions?.priceChange || 
                'For already approved requests, use "change" to update the price'}
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.roomUpgrade?.bulkAnswer?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file}
            className={`px-6 py-2 rounded-lg text-white
              ${!file
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {translations?.roomUpgrade?.bulkAnswer?.upload || 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};